import React from 'react';
import { Box } from '@material-ui/core';
import { useTracking, TrackEventName, TrackPageName } from 'modules/tracking';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Button, Typography, TypographyVariant } from 'components/core';
import { useOnboardingContext } from 'modules/onboarding/v3-streamlined-onboarding/streamlined-onboarding';
import { PageTitle } from '../components/page-title';
import {
  CheckboxController,
  FormFieldContainer,
  SubmitButton,
  TextFieldController,
  formFields,
} from '../components/forms';

interface PublicCompanyTickerField {
  affiliationId?: string;
  ticker: string;
}

interface SavedPublicCompanyControlAffiliation {
  id?: string;
  ticker?: string;
}

interface BusinessRepresentativeForm {
  brokerDealer: boolean;
  haveInterestedPartiesToReport: boolean;
  largeTrader: boolean;
  leaderOfPublicCompany: boolean;
  politicallyExposed: boolean;
  publicCompanyTickers: PublicCompanyTickerField[];
  stockExchangeFinraMemberAssociated: boolean;
}

export const RareScenarios: React.FC = () => {
  const { trackEvent } = useTracking<{
    page: TrackPageName;
    eventName: TrackEventName;
  }>({
    page: TrackPageName.RareScenarios,
  });

  const onboardingContext = useOnboardingContext();
  const savedOnboardingData = onboardingContext.onboardingResponse.data;
  const savedBusinessRepresentativeData = savedOnboardingData.people?.filter(
    (person: { isPersonOnboarding: boolean }) =>
      person.isPersonOnboarding === true,
  )[0];
  const savedAffiliations: SavedPublicCompanyControlAffiliation[] =
    savedOnboardingData.publicCompanyControlAffiliations || [];

  const { control, handleSubmit, watch } = useForm<BusinessRepresentativeForm>({
    defaultValues: {
      brokerDealer: savedOnboardingData.personOnboarding?.brokerDealer || false,
      haveInterestedPartiesToReport:
        savedOnboardingData.business?.haveInterestedPartiesToReport || false,
      largeTrader: savedOnboardingData.personOnboarding?.largeTrader || false,
      leaderOfPublicCompany:
        savedOnboardingData.personOnboarding?.leaderOfPublicCompany || false,
      politicallyExposed:
        savedOnboardingData.personOnboarding?.politicallyExposed || false,
      stockExchangeFinraMemberAssociated:
        savedOnboardingData.personOnboarding
          ?.stockExchangeFinraMemberAssociated || false,
      publicCompanyTickers:
        savedAffiliations.length > 0
          ? savedAffiliations.map((affiliation) => ({
              affiliationId: affiliation.id,
              ticker: affiliation.ticker || '',
            }))
          : [{ ticker: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'publicCompanyTickers',
  });

  const leaderOfPublicCompany = watch('leaderOfPublicCompany');

  const onSubmit = (data: BusinessRepresentativeForm) => {
    const {
      haveInterestedPartiesToReport,
      publicCompanyTickers,
      ...personOnboarding
    } = data;

    const payload: {
      business: { haveInterestedPartiesToReport: boolean };
      personOnboarding: Omit<
        BusinessRepresentativeForm,
        'haveInterestedPartiesToReport' | 'publicCompanyTickers'
      >;
      people: Array<
        Omit<
          BusinessRepresentativeForm,
          'haveInterestedPartiesToReport' | 'publicCompanyTickers'
        > & { id?: string }
      >;
      publicCompanyControlAffiliations?: Array<{ id?: string; ticker: string }>;
    } = {
      business: {
        haveInterestedPartiesToReport,
      },
      personOnboarding,
      people: [
        {
          ...personOnboarding,
          id: savedBusinessRepresentativeData?.id,
        },
      ],
    };

    if (personOnboarding.leaderOfPublicCompany) {
      payload.publicCompanyControlAffiliations = (publicCompanyTickers || [])
        .map((item) => ({
          id: item.affiliationId || undefined,
          ticker: (item.ticker || '').trim().toUpperCase(),
        }))
        .filter((item) => item.ticker);
    }

    onboardingContext.postOnboardingData({
      businessId: onboardingContext.businessId,
      data: payload,
    });

    trackEvent({
      eventName: TrackEventName.RareScenariosCompleted,
    });
  };

  return (
    <>
      <PageTitle
        title="Do any of these happen to apply?"
        description="If these terms are not familiar, these cases likely do not apply and you can continue."
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormFieldContainer>
          <CheckboxController
            {...formFields.leaderOfPublicCompany}
            control={control}
            defaultValue={
              savedOnboardingData.personOnboarding?.leaderOfPublicCompany ||
              false
            }
          />
        </FormFieldContainer>

        {leaderOfPublicCompany && (
          <FormFieldContainer>
            <Box mb={1}>
              <Typography color="midGray" variant={TypographyVariant.SubLabel}>
                Enter ticker symbols of other public companies where you are a
                director, officer, or more than 10% owner — not the business
                being onboarded.
              </Typography>
            </Box>
            {fields.map((field, index) => (
              <Box key={field.id} mb={2}>
                <Controller
                  name={`publicCompanyTickers.${index}.affiliationId`}
                  control={control}
                  defaultValue={field.affiliationId || ''}
                  render={({ field: affiliationIdField }) => (
                    <input type="hidden" {...affiliationIdField} />
                  )}
                />
                <TextFieldController
                  {...formFields.publicCompanyTicker}
                  name={`publicCompanyTickers.${index}.ticker`}
                  control={control}
                  defaultValue={field.ticker}
                />
                {fields.length > 1 && (
                  <Box mt={1}>
                    <Button
                      type="button"
                      variant="text"
                      onClick={() => remove(index)}
                    >
                      Remove ticker
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
            <Button
              type="button"
              variant="text"
              onClick={() => append({ ticker: '' })}
            >
              Add another ticker
            </Button>
          </FormFieldContainer>
        )}

        <FormFieldContainer>
          <CheckboxController
            {...formFields.brokerDealer}
            control={control}
            defaultValue={
              savedOnboardingData.personOnboarding?.brokerDealer || false
            }
          />
        </FormFieldContainer>

        <FormFieldContainer>
          <CheckboxController
            {...formFields.largeTrader}
            control={control}
            defaultValue={
              savedOnboardingData.personOnboarding?.largeTrader || false
            }
          />
        </FormFieldContainer>

        <FormFieldContainer>
          <CheckboxController
            {...formFields.stockExchangeFinraMemberAssociated}
            control={control}
            defaultValue={
              savedOnboardingData.personOnboarding
                ?.stockExchangeFinraMemberAssociated || false
            }
          />
        </FormFieldContainer>

        <FormFieldContainer>
          <CheckboxController
            {...formFields.haveInterestedPartiesToReport}
            control={control}
            defaultValue={
              savedOnboardingData.business?.haveInterestedPartiesToReport ||
              false
            }
          />
        </FormFieldContainer>

        <FormFieldContainer>
          <CheckboxController
            {...formFields.politicallyExposed}
            control={control}
            defaultValue={
              savedOnboardingData.personOnboarding?.politicallyExposed || false
            }
          />
        </FormFieldContainer>

        <SubmitButton />
      </form>
    </>
  );
};
